import React, { useState, useEffect, useRef } from "react";
import { Paper, Typography, Divider, Grid, Button, Checkbox, Box } from "@mui/material";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  TableSortLabel,
  Tooltip
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CommonLoader from "../common/CommonLoader";
import ViewQuote from "./ViewQuote";
import DeleteConfirmation from "../common/DeleteConfirmation";
import DuplicateQuoteDialog from "./DuplicateQuoteDialog";
import { NotificationManager } from "react-notifications";
import {
  DeleteQuote,
  SendBulkQuoteEmails,
  DuplicateQuoteById,
} from "../../services/QuotesService";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import {
  getComparator,
  stableSort,
} from "../../../common-utilities/sortingUtils";
import { useNavigate } from "react-router-dom";

const QuotesTable = ({ quotesData, count, onChange, onViewModeChanged }) => {
  const columns = [
    "Select",
    "Quote",
    "Name",
    "Description",
    "Contact",
    "Type",
    "Status",
    "Start",
    "Expiration",
    "Actions",
  ];
  const columnHeaders = [
    "",
    "quoteNumber",
    "billingName",
    "description",
    "billingContact",
    "projectType",
    "status",
    "startDate",
    "expirationDate",

  ];
  const rowsPerPageOptions = [20, 50, 100, 150, 200, 400, 600, 800, 1000];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedQuoteToDelete, setSelectedQuoteToDelete] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [selectedQuotes, setSelectedQuotes] = useState([]);
  const [tableHeight, setTableHeight] = useState(470);
  // Quote number the duplicate dialog is open for, null when it's closed
  const [quoteToDuplicate, setQuoteToDuplicate] = useState(null);
  const [isDuplicating, setIsDuplicating] = useState(false);

  const tableContainerRef = useRef(null);
  const navigate = useNavigate();

  // Calculate dynamic table height
  useEffect(() => {
    const calculateTableHeight = () => {
      if (tableContainerRef.current) {
        const containerTop = tableContainerRef.current.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        // Reserve space for pagination (56px) and some bottom padding (20px)
        const availableHeight = windowHeight - containerTop - 76;
        // Set minimum height to 300px and maximum based on available space
        const calculatedHeight = Math.max(300, Math.min(availableHeight, windowHeight - 200));
        setTableHeight(calculatedHeight);
      }
    };

    calculateTableHeight();
    window.addEventListener('resize', calculateTableHeight);

    // Recalculate when data changes
    const timeoutId = setTimeout(calculateTableHeight, 100);

    return () => {
      window.removeEventListener('resize', calculateTableHeight);
      clearTimeout(timeoutId);
    };
  }, [quotesData, selectedQuotes]);

  useEffect(() => {
    handleChange();
  }, [page, rowsPerPage]);

  // Clear selections when page or data changes
  useEffect(() => {
    setSelectedQuotes([]);
  }, [page, rowsPerPage, quotesData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChange = () => {
    onChange(page, rowsPerPage, orderBy, order);
  };
  
  const formatDateOnly = (dateValue) => {
    if (!dateValue) return "";
    return new Date(dateValue).toLocaleDateString();
  };
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = quotesData.map((quote) => quote.quoteNumber);
      setSelectedQuotes(newSelected);
      return;
    }
    setSelectedQuotes([]);
  };

  const handleCheckboxClick = (quoteNumber) => {
    const selectedIndex = selectedQuotes.indexOf(quoteNumber);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedQuotes, quoteNumber);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedQuotes.slice(1));
    } else if (selectedIndex === selectedQuotes.length - 1) {
      newSelected = newSelected.concat(selectedQuotes.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedQuotes.slice(0, selectedIndex),
        selectedQuotes.slice(selectedIndex + 1)
      );
    }

    setSelectedQuotes(newSelected);
  };

  const isSelected = (quoteNumber) => selectedQuotes.indexOf(quoteNumber) !== -1;

  const handleBulkSendQuotes = async () => {
    if (selectedQuotes.length === 0) {
      NotificationManager.warning("Please select at least one quote to send");
      return;
    }

    try {
      NotificationManager.success(`Sending ${selectedQuotes.length} quote(s)`);
      setLoading(true);
      const response = await SendBulkQuoteEmails(selectedQuotes);
      if (response.results === true) {
        NotificationManager.success(`${selectedQuotes.length} quote(s) sent successfully`);
        setSelectedQuotes([]);
        handleChange();
      } else {
        NotificationManager.error("Error sending quotes");
      }
    } catch (error) {
      NotificationManager.error("Error sending quotes");
    } finally {
      setLoading(false);
    }
  };

  const handleViewClick = (quote) => {
    setSelectedQuote(quote);
    onViewModeChanged();
  };

  const handleSendQuoteClick = (quoteNumber) => {
    handleSendQuoteEmail(quoteNumber);
  };

  const handleSendQuoteEmail = async (quoteNumber) => {
    try {
      NotificationManager.success("Sending Quote");
      setLoading(true);
      const response = await SendBulkQuoteEmails([quoteNumber]);
      if (response.results === true) {
        NotificationManager.success("Quote sent successfully");
        handleChange();
      } else {
        NotificationManager.error("Error sending quote");
      }
    } catch (error) {
      NotificationManager.error("Error sending quote");
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    setSelectedQuote(null);
    onViewModeChanged();
  };

  const handleDeleteClick = (quoteNumber) => {
    setSelectedQuoteToDelete(quoteNumber);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      setLoading(true);
      const response = await DeleteQuote(selectedQuoteToDelete);
      if (response.results === true) {
        NotificationManager.success("Quote deleted successfully");
        setIsDeleteConfirmationOpen(false);
        handleChange();
      } else {
        NotificationManager.error("Error deleting quote");
      }
    } catch (error) {
      NotificationManager.error("Error deleting quote");
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateClick = (quoteNumber) => {
    setQuoteToDuplicate(quoteNumber);
  };

  const handleDuplicateConfirm = async (copies) => {
    try {
      setIsDuplicating(true);
      const response = await DuplicateQuoteById(quoteToDuplicate, copies);
      const newQuoteNumbers = response?.results || [];

      if (newQuoteNumbers.length > 0) {
        NotificationManager.success(
          `${newQuoteNumbers.length} quote(s) created: ${newQuoteNumbers.join(", ")}`
        );
        setQuoteToDuplicate(null);
        // Pull the page again so the new quotes show up in the results
        handleChange();
      } else {
        NotificationManager.error("Error duplicating quote");
      }
    } catch (error) {
      NotificationManager.error("Error duplicating quote");
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleRequestSort = (property) => {
    if (property === "") return; // Skip sorting for checkbox column
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const renderActions = (quote) => (
    <div style={{ minWidth: "200px" }}>
      <Tooltip title="Send Quote" arrow>
        <IconButton
          aria-label="send-quote"
          style={{ color: "#8911f2" }}
          onClick={() => handleSendQuoteClick(quote.quoteNumber)}
        >
          <ForwardToInboxIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Duplicate Quote" arrow>
        <IconButton
          aria-label="duplicate-quote"
          style={{ color: "#1976d2" }}
          onClick={() => handleDuplicateClick(quote.quoteNumber)}
        >
          <ContentCopyIcon />
        </IconButton>
      </Tooltip>

      <IconButton
        aria-label="view"
        style={{ color: "green" }}
        onClick={() => handleViewClick(quote)}
      >
        <VisibilityIcon />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(quote.quoteNumber)}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  return (
    <>
      {selectedQuote ? (
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewQuote quote={selectedQuote} />
        </>
      ) : (
        <>
          {selectedQuotes.length > 0 && (
            <div style={{ marginBottom: "16px", marginRight: "20px" }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<ForwardToInboxIcon />}
                onClick={handleBulkSendQuotes}
                style={{ backgroundColor: "#8911f2" }}
              >
                Send Quotes ({selectedQuotes.length})
              </Button>
            </div>
          )}
          <Paper style={{ marginRight: "20px" }}>
            <TableContainer
              ref={tableContainerRef}
              sx={{
                maxHeight: tableHeight,
                overflowY: "auto"
              }}
            >
              <Table sx={{ minWidth: 400 }} size="small" stickyHeader>
                <TableHead
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="default"
                        indeterminate={
                          selectedQuotes.length > 0 &&
                          selectedQuotes.length < quotesData.length
                        }
                        checked={
                          quotesData.length > 0 &&
                          selectedQuotes.length === quotesData.length
                        }
                        onChange={handleSelectAllClick}
                        inputProps={{
                          "aria-label": "select all quotes",
                        }}
                        style={{ color: "white" }}
                      />
                    </TableCell>
                    {columns.slice(1).map((column, index) => (
                      <TableCell key={column}>
                        {column !== "Actions" ? (
                          <TableSortLabel
                            active={orderBy === columnHeaders[index + 1]}
                            direction={
                              orderBy === columnHeaders[index + 1]
                                ? order
                                : "asc"
                            }
                            onClick={() =>
                              handleRequestSort(columnHeaders[index + 1])
                            }
                          >
                            {column}
                          </TableSortLabel>
                        ) : (
                          column
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stableSort(quotesData, getComparator(order, orderBy)).map(
                    (quote, index) => {
                      const isItemSelected = isSelected(quote.quoteNumber);
                      return (
                        <TableRow
                          key={quote.quoteNumber}
                          sx={{
                            backgroundColor:
                              index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              color="primary"
                              checked={isItemSelected}
                              onChange={() =>
                                handleCheckboxClick(quote.quoteNumber)
                              }
                              inputProps={{
                                "aria-labelledby": `checkbox-${quote.quoteNumber}`,
                              }}
                            />
                          </TableCell>
                          {columnHeaders.slice(1).map((column) => (
                            <TableCell key={column}>
                              {column === "quoteNumber" ? (
                                <a
                                  href={`/vlp/quotes/${quote.quoteNumber}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: "#1976d2",
                                    textDecoration: "none",
                                    cursor: "pointer",
                                    fontWeight: 500
                                  }}
                                  onMouseEnter={(e) => (e.target.style.textDecoration = "underline")}
                                  onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
                                >
                                  {quote[column]}
                                </a>
                              ) : column === "description" ? (
                                <Tooltip title={quote[column]?.trim() || ""} arrow>
                                  <span>
                                    {quote[column]?.trim().substring(0, 20)}
                                    {quote[column]?.trim().length > 20 ? "..." : ""}
                                  </span>
                                </Tooltip>
                              ) : column === "startDate" || column === "expirationDate" ? (
                                quote[column] ? formatDateOnly(quote[column]) : ""
                              ) : (
                                quote[column]
                              )}
                            </TableCell>
                          ))}
                          <TableCell>{renderActions(quote)}</TableCell>
                        </TableRow>
                      );
                    }
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={count}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
          <CommonLoader loading={loading} />
        </>
      )}
      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message="Are you sure you want to delete this quote?"
        color="red"
      />

      <DuplicateQuoteDialog
        open={Boolean(quoteToDuplicate)}
        quoteNumber={quoteToDuplicate}
        loading={isDuplicating}
        onClose={() => setQuoteToDuplicate(null)}
        onConfirm={handleDuplicateConfirm}
      />
    </>
  );
};

export default QuotesTable;